import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/layout/AppLayout';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Avatar } from '../components/ui/Avatar';
import { Badge } from '../components/ui/Badge';
import { SplitwiseConnectButton } from '../components/features/SplitwiseConnectButton';

const Profile: React.FC = () => {
  const { user, logout } = useAuth();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = () => {
    logout();
  };

  return (
    <AppLayout>
      <Header title="Profile" />

      <main className="p-4 pb-24 space-y-6">
        {/* Profile Card */}
        <Card>
          <div className="flex items-center gap-4 mb-6">
            <Avatar name={user?.name || 'User'} size="lg" />
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-slate-900 truncate">{user?.name}</h2>
              <p className="text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>

          {/* Dietary Preferences */}
          <div className="border-t border-slate-100 pt-4">
            <h3 className="text-sm font-medium text-slate-700 mb-3">Default Preferences</h3>
            <div className="flex gap-2">
              {user?.defaultIsVegetarian ? (
                <Badge variant="veg">Vegetarian</Badge>
              ) : (
                <Badge variant="nonveg">Non-vegetarian</Badge>
              )}
              {user?.defaultDrinksAlcohol && (
                <Badge variant="alcohol">Drinks Alcohol</Badge>
              )}
            </div>
          </div>
        </Card>

        {/* Settings Section */}
        <section>
          <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3 px-1">
            Settings
          </h3>
          <Card padding="none">
            <div className="divide-y divide-slate-100">
              <button className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors text-left">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">Edit Profile</p>
                  <p className="text-sm text-slate-500">Update your name and preferences</p>
                </div>
                <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors text-left">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">Notifications</p>
                  <p className="text-sm text-slate-500">Manage notification preferences</p>
                </div>
                <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors text-left">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">Payment Methods</p>
                  <p className="text-sm text-slate-500">Add UPI, cards, or bank accounts</p>
                </div>
                <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </Card>
        </section>

        {/* Integrations Section */}
        <section>
          <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3 px-1">
            Integrations
          </h3>
          <SplitwiseConnectButton />
        </section>

        {/* Support Section */}
        <section>
          <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3 px-1">
            Support
          </h3>
          <Card padding="none">
            <div className="divide-y divide-slate-100">
              <button className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors text-left">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">Help & FAQ</p>
                  <p className="text-sm text-slate-500">Get help with common questions</p>
                </div>
                <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors text-left">
                <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">About</p>
                  <p className="text-sm text-slate-500">App version and info</p>
                </div>
                <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </Card>
        </section>

        {/* Logout Button */}
        <Button
          variant="secondary"
          fullWidth
          onClick={() => setShowLogoutConfirm(true)}
          className="text-red-600 border-red-200 hover:bg-red-50"
        >
          Log Out
        </Button>
      </main>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <Card className="w-full max-w-sm">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Log out?</h3>
            <p className="text-slate-500 mb-6">Are you sure you want to log out of your account?</p>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                fullWidth
                onClick={() => setShowLogoutConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700"
              >
                Log Out
              </Button>
            </div>
          </Card>
        </div>
      )}
    </AppLayout>
  );
};

export default Profile;
